import type { Tour, TourStep } from '../tour-state.js';

// My Account's own tour, on the first visit once the welcome tour is done. It explains the left-hand
// column, then opens each tab in turn and explains its sections. Sections only some people see are
// optional: left out when they aren't on the page.
const step = (fields: Omit<TourStep, 'advance' | 'passive'>): TourStep => ({
  advance: 'next',
  passive: true,
  ...fields,
});

// The first step on a tab opens it; the rest of that tab's steps find it already open
const onTab = (tab: string, steps: Omit<TourStep, 'advance' | 'passive'>[]): TourStep[] =>
  steps.map((fields) => step({ select: `account-tab-${tab}`, group: tab, ...fields }));

const account: Tour = {
  id: 'account',
  steps: [
    step({
      id: 'account-personal',
      targets: ['account-personal'],
      text: 'Your name and email. Edit changes your name.',
    }),
    step({
      id: 'account-settings',
      targets: ['account-settings'],
      text: 'Reduce motion turns animations down. Show walkthrough brings these tours back.',
    }),
    step({ id: 'account-company', targets: ['account-company'], text: 'The company the tabs below are about.' }),
    step({
      id: 'account-tabs',
      targets: ['account-tabs'],
      text: 'These tabs switch between your company, its public ledger, its subscription and your notifications.',
    }),
    ...onTab('company', [
      {
        id: 'account-my-details',
        targets: ['account-my-details'],
        text: 'Your name, your role at the company and your email.',
      },
      {
        id: 'account-address',
        targets: ['account-address'],
        text: 'Where the company is registered, and its registered number.',
      },
      {
        id: 'account-company-signatories',
        targets: ['account-company-signatories'],
        text: "The people who must sign any change to the company's public ledger account.",
        optional: true,
      },
      {
        id: 'account-topic-signatories',
        targets: ['account-topic-signatories'],
        text: "The people who must sign the company's messages on the public ledger. They can be the company's signatories or different people.",
        optional: true,
      },
      {
        id: 'account-relationships',
        targets: ['account-clients', 'account-suppliers'],
        text: 'Companies you work with: your clients and your suppliers.',
        optional: true,
      },
      { id: 'account-colleagues', targets: ['account-colleagues'], text: 'Everyone else at the company.' },
      {
        // People who manage roles see everyone's roles here; everyone else sees their own
        id: 'account-roles',
        targets: ['account-roles'],
        text: 'What each person can do comes from their roles. If you manage roles, you give and take them here; otherwise this shows yours.',
      },
      { id: 'account-teams', targets: ['account-teams'], text: "The company's teams, which jobs can be shared with." },
      {
        // The overdue lists (#37) show to admins and to people who manage a team or have direct reports
        id: 'account-overdue-team',
        targets: ['account-overdue-team'],
        text: "Team jobs open for more than 7 days, by who holds them. They lower your company's trust score, more so when another company is waiting.",
        optional: true,
      },
      {
        id: 'account-overdue-individual',
        targets: ['account-overdue-individual'],
        text: 'The other overdue jobs: those in no team, and those held by people who report to you.',
        optional: true,
      },
      {
        id: 'account-open-jobs',
        targets: ['account-open-jobs'],
        text: 'The open jobs you manage: move one to someone else, or put it back for its team to claim.',
        optional: true,
      },
    ]),
    ...onTab('public-ledger', [
      {
        id: 'account-ledger',
        targets: ['account-ledger'],
        text: "The company's topic on the public ledger, then your own account, topic and public key, which are yours alone.",
      },
      {
        // The Graph card shows only to the company's public ledger signatories
        id: 'account-graph',
        targets: ['account-graph'],
        text: "How the company's credits have moved: the balances of the company and operating accounts, and credits in and out. Switch between the last 30 days and the last 12 months.",
        optional: true,
      },
      {
        id: 'account-credits',
        targets: ['account-credits'],
        text: "The company's nodeStream credits, and those on the way.",
      },
      {
        id: 'account-devices',
        targets: ['account-devices'],
        text: "The devices signed in to your account. Sign out of one you don't use.",
      },
    ]),
    ...onTab('subscription', [
      {
        id: 'account-active-subscription',
        targets: ['account-active-subscription'],
        text: 'Your plan, its fee and when the next payment is due.',
        optional: true,
      },
      {
        id: 'account-payment-methods',
        targets: ['account-payment-methods'],
        text: 'The cards your subscription can be paid with.',
        optional: true,
      },
      {
        id: 'account-subscriptions',
        targets: ['account-subscriptions'],
        text: 'Every subscription the company has.',
        optional: true,
      },
      {
        id: 'account-invoices',
        targets: ['account-invoices'],
        text: 'Your invoices, to search or download.',
        optional: true,
      },
      {
        id: 'account-no-subscription',
        targets: ['account-no-subscription'],
        text: 'The company has no subscription yet.',
        optional: true,
      },
    ]),
    ...onTab('notifications', [
      { id: 'account-email', targets: ['account-email'], text: 'Which notifications are emailed to you, and when.' },
    ]),
  ],
};

export default account;
